var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import moment from "moment";
import useSWR, { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";
import { dateFilters } from "../../Account/constants";
import { LogLevel } from "../../Shared/hooks/useLogging/Logging-types";
import { useLogging } from "../../Shared/hooks/useLogging/useLogging";
import useToast from "../../hooks/useToast";
import { apFetcher } from "../Utilities/axiosPrivateFetcher_SWR";
import { refreshMultipleCaches } from "../Utilities/refreshMultipleCaches";
import { SWR_KEYS } from "../Utilities/swrKeys";
import { filterOrdersKeys } from "./swrOrders-utils";
import { updateFunction_V2 } from "./updateFunction_V2";
export const useOrdersQuery = (pageNumber, dateFilter, itemsPerPage = 10) => {
    const toast = useToast();
    const { datadogLog } = useLogging();
    const { cache, mutate } = useSWRConfig();
    const pageIndex = pageNumber - 1;
    const pageIndexKey = `page-index-${pageIndex.toString()}`;
    const upcomingOrdersKey = `${SWR_KEYS.UPCOMING_ORDERS}-${pageIndexKey}`;
    const byDateOrdersKey = `${SWR_KEYS.ORDERS_BY_DATE}-${pageIndexKey}`;
    const allTime = dateFilter > dateFilters.oneYear;
    const startDate = allTime ? moment() : moment().subtract(dateFilter, "months");
    const startDateISO = startDate.clone().toISOString();
    const endDate = allTime ? moment() : moment().add(1, "day");
    const endDateISO = endDate.clone().toISOString();
    const fetchAllOrders = allTime ? true : false;
    //* ===== SWR PRELOADING
    const preloadIndex = pageIndex + 1;
    const preloadPageIndexKey = `page-index-${preloadIndex.toString()}`;
    const preloadUpcomingKey = `${SWR_KEYS.UPCOMING_ORDERS}-${preloadPageIndexKey}`;
    const preloadUpcomingQueryString = `/Account/GetUpcomingOrders?pageIndex=${preloadIndex}&pageSize=${itemsPerPage}`;
    const preloadByDateKey = `${SWR_KEYS.ORDERS_BY_DATE}-${preloadPageIndexKey}`;
    const preloadByDateQueryString = `/Account/GetOrders?from=${startDateISO}&to=${endDateISO}&fetchAllOrders=${fetchAllOrders}&pageIndex=${preloadIndex}&pageSize=${itemsPerPage}`;
    //* PRELOAD UPCOMING ORDERS
    useSWR(preloadUpcomingKey, // SWR KEY is bound to the specific page index
    apFetcher(preloadUpcomingQueryString));
    //* PRELOAD BY DATE ORDERS
    useSWR(preloadByDateKey, // SWR KEY is bound to the specific page index
    apFetcher(preloadByDateQueryString));
    //* ===== SWR UPCOMING ORDERS QUERY =====
    const upcomingQueryString = `/Account/GetUpcomingOrders?pageIndex=${pageIndex}&pageSize=${itemsPerPage}`;
    const upcomingOrdersQueryResponse = useSWR(upcomingOrdersKey, // SWR KEY is bound to the specific page index
    apFetcher(upcomingQueryString));
    //* ===== SWR ORDERS BY DATE QUERY =====
    const byDateQueryString = `/Account/GetOrders?from=${startDateISO}&to=${endDateISO}&fetchAllOrders=${fetchAllOrders}&pageIndex=${pageIndex}&pageSize=${itemsPerPage}`;
    const ordersByDateQueryResponse = useSWR(byDateOrdersKey, // SWR KEY is bound to the specific page index
    apFetcher(byDateQueryString));
    //* ===== SWR UPCOMING ORDERS MUTATION =====
    const MUTATION_KEY = dateFilter === dateFilters.upcoming ? upcomingOrdersKey : byDateOrdersKey;
    const { trigger: triggerUpdate } = useSWRMutation(MUTATION_KEY, updateFunction_V2, {
        onSuccess: (data) => {
            var _a;
            if (!((_a = data === null || data === void 0 ? void 0 : data.balance) === null || _a === void 0 ? void 0 : _a.remainingBalance) || data.balance.remainingBalance <= 0) {
                // ? see function for explanation of why we do this
                refreshMultipleCaches(cache, mutate, filterOrdersKeys);
            }
        },
        onError: (error) => {
            datadogLog("Orders update error", LogLevel.ERROR, undefined, error);
        },
        // Don't revalidate here, as we handle it manually (above)
        revalidate: false,
    });
    //* ===== SWR UPCOMING ORDERS MUTATION TRIGGER FUNCTION =====
    const triggerOrderUpdate = (newAddressData, balance, giftCardId, onSuccess, onError) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const data = yield triggerUpdate({ newAddressData, giftCardId, balance });
            if (onSuccess)
                onSuccess(data);
            else
                toast.push(`Successfully updated order!`, { variant: "success" });
        }
        catch (err) {
            if (onError)
                onError(err);
            else
                toast.push("Failed to update order.", { variant: "error" });
        }
    });
    return {
        upcomingOrdersQueryResponse,
        ordersByDateQueryResponse,
        triggerOrderUpdate,
        isMutating: false,
    };
};
